import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import axios from 'axios';
import { ChevronRight, DollarSign, Target, TrendingDown, X, Building2, Trophy, User, Calendar, FileText, Package, ArrowLeft, Phone, Mail } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGES = [
  { name: "Initial Conversation", color: "#007AFF", bgColor: "#EFF6FF" },
  { name: "Qualified Opportunity", color: "#8B5CF6", bgColor: "#F5F3FF" },
  { name: "Discovery", color: "#06B6D4", bgColor: "#ECFEFF" },
  { name: "Quote", color: "#F59E0B", bgColor: "#FFFBEB" },
  { name: "Negotiation", color: "#F97316", bgColor: "#FFF7ED" },
  { name: "Closed Won", color: "#10B981", bgColor: "#ECFDF5" },
  { name: "Closed Lost", color: "#EF4444", bgColor: "#FEF2F2" }
];

const PipelinePage = () => {
  const { getAuthHeaders } = useAuth();
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStage, setSelectedStage] = useState(null);
  const [stageOpportunities, setStageOpportunities] = useState([]);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);

  useEffect(() => {
    fetchOpportunities();
  }, []);

  const fetchOpportunities = async () => {
    try {
      const response = await axios.get(`${API}/opportunities`, {
        headers: getAuthHeaders()
      });
      setOpportunities(response.data);
    } catch (error) {
      console.error('Failed to fetch opportunities:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStageData = (stageName) => {
    const stageOpps = opportunities.filter(opp => opp.stage === stageName);
    return {
      count: stageOpps.length,
      value: stageOpps.reduce((sum, opp) => sum + (opp.total_estimated_premium || 0), 0),
      opportunities: stageOpps
    };
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';
  };

  const handleStageClick = (stage) => {
    const data = getStageData(stage.name);
    setSelectedStage(stage);
    setStageOpportunities(data.opportunities);
  };

  const closeDialog = () => {
    setSelectedStage(null);
    setStageOpportunities([]);
  };

  const openOpportunityDetail = (opp) => {
    setSelectedOpportunity(opp);
  };

  const closeOpportunityDetail = () => {
    setSelectedOpportunity(null);
  };

  const getStageInfo = (stageName) => {
    return STAGES.find(s => s.name === stageName) || STAGES[0];
  };

  // Calculate max value for funnel width scaling
  const maxValue = Math.max(...STAGES.slice(0, 5).map(s => getStageData(s.name).value), 1);

  // Calculate totals
  const totalPipeline = opportunities
    .filter(o => o.status === 'Open')
    .reduce((sum, o) => sum + (o.total_estimated_premium || 0), 0);
  const totalWon = opportunities
    .filter(o => o.status === 'Closed Won')
    .reduce((sum, o) => sum + (o.total_estimated_premium || 0), 0);
  const totalLost = opportunities
    .filter(o => o.status === 'Closed Lost')
    .reduce((sum, o) => sum + (o.total_estimated_premium || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-pulse-slow text-[#007AFF] text-xl">Loading pipeline...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="pipeline-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-extrabold text-[#0F172A] tracking-tight" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
          SALES PIPELINE
        </h1>
        <p className="text-[#475569] mt-1">Funnel view of opportunities by stage</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-white border-[#E2E8F0] shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#EFF6FF] flex items-center justify-center">
                <Target className="w-5 h-5 text-[#007AFF]" />
              </div>
              <div>
                <p className="text-xs text-[#94A3B8] uppercase tracking-wider font-medium">Active Pipeline</p>
                <p className="text-2xl font-bold text-[#0F172A]" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                  {formatCurrency(totalPipeline)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white border-[#E2E8F0] shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#ECFDF5] flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-[#10B981]" />
              </div>
              <div>
                <p className="text-xs text-[#94A3B8] uppercase tracking-wider font-medium">Closed Won</p>
                <p className="text-2xl font-bold text-[#10B981]" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                  {formatCurrency(totalWon)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white border-[#E2E8F0] shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#FEF2F2] flex items-center justify-center">
                <TrendingDown className="w-5 h-5 text-[#EF4444]" />
              </div>
              <div>
                <p className="text-xs text-[#94A3B8] uppercase tracking-wider font-medium">Closed Lost</p>
                <p className="text-2xl font-bold text-[#EF4444]" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                  {formatCurrency(totalLost)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Funnel View */}
      <Card className="bg-white border-[#E2E8F0] shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg text-[#0F172A]" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            PIPELINE FUNNEL
          </CardTitle>
          <p className="text-sm text-[#94A3B8]">Click on any stage to view opportunities</p>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="space-y-3">
            {STAGES.map((stage, index) => {
              const data = getStageData(stage.name);
              const isClosedStage = stage.name === 'Closed Won' || stage.name === 'Closed Lost';
              
              // Calculate funnel width - wider at top, narrower at bottom for active stages
              // Closed stages are separate
              let widthPercent;
              if (isClosedStage) {
                widthPercent = 50;
              } else {
                // Funnel effect: starts at 100% and decreases
                const funnelIndex = STAGES.slice(0, 5).findIndex(s => s.name === stage.name);
                widthPercent = Math.max(40, 100 - (funnelIndex * 12));
              }
              
              // Also consider value-based width
              const valueWidth = data.value > 0 ? Math.max(30, (data.value / maxValue) * 100) : 30;
              const finalWidth = isClosedStage ? widthPercent : Math.max(widthPercent * 0.5, valueWidth);

              return (
                <div key={stage.name} className="relative">
                  {/* Separator before Closed stages */}
                  {stage.name === 'Closed Won' && (
                    <div className="border-t border-dashed border-[#E2E8F0] my-6 pt-2">
                      <span className="text-xs text-[#94A3B8] uppercase tracking-wider">Closed Deals</span>
                    </div>
                  )}
                  
                  <button
                    onClick={() => handleStageClick(stage)}
                    className="w-full group"
                    data-testid={`stage-${stage.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <div 
                      className="relative mx-auto transition-all duration-300 group-hover:scale-[1.02] group-hover:shadow-md rounded-lg overflow-hidden cursor-pointer"
                      style={{ 
                        width: `${finalWidth}%`,
                        minWidth: '200px'
                      }}
                    >
                      {/* Stage Bar */}
                      <div 
                        className="flex items-center justify-between p-4 rounded-lg border-2 transition-colors"
                        style={{ 
                          backgroundColor: stage.bgColor,
                          borderColor: data.count > 0 ? stage.color : '#E2E8F0'
                        }}
                      >
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: stage.color }}
                          />
                          <div className="text-left">
                            <p className="font-semibold text-[#0F172A]">{stage.name}</p>
                            <p className="text-sm text-[#475569]">
                              {data.count} {data.count === 1 ? 'opportunity' : 'opportunities'}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <p 
                              className="text-xl font-bold"
                              style={{ 
                                fontFamily: "'Barlow Condensed', sans-serif",
                                color: stage.color 
                              }}
                            >
                              {formatCurrency(data.value)}
                            </p>
                          </div>
                          <ChevronRight 
                            className="w-5 h-5 text-[#94A3B8] group-hover:text-[#0F172A] transition-colors" 
                          />
                        </div>
                      </div>
                    </div>
                  </button>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Stage Detail Dialog */}
      <Dialog open={!!selectedStage} onOpenChange={closeDialog}>
        <DialogContent className="bg-white border-[#E2E8F0] text-[#0F172A] max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div 
                  className="w-4 h-4 rounded-full"
                  style={{ backgroundColor: selectedStage?.color }}
                />
                <DialogTitle className="text-xl font-bold" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                  {selectedStage?.name.toUpperCase()}
                </DialogTitle>
                <Badge 
                  className="border-0"
                  style={{ 
                    backgroundColor: selectedStage?.bgColor,
                    color: selectedStage?.color 
                  }}
                >
                  {stageOpportunities.length} {stageOpportunities.length === 1 ? 'opportunity' : 'opportunities'}
                </Badge>
              </div>
            </div>
          </DialogHeader>
          
          <ScrollArea className="max-h-[60vh] mt-4">
            {stageOpportunities.length === 0 ? (
              <div className="text-center py-12 text-[#94A3B8]">
                <Target className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No opportunities in this stage</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-[#E2E8F0] hover:bg-transparent bg-[#F8FAFC]">
                    <TableHead className="text-[#475569] font-semibold">University / Sport</TableHead>
                    <TableHead className="text-[#475569] font-semibold">Owner</TableHead>
                    <TableHead className="text-[#475569] font-semibold">Products</TableHead>
                    <TableHead className="text-[#475569] font-semibold text-right">Premium</TableHead>
                    <TableHead className="text-[#475569] font-semibold">Close Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stageOpportunities.map((opp) => (
                    <TableRow 
                      key={opp.id} 
                      className="border-[#E2E8F0] hover:bg-[#F8FAFC] cursor-pointer transition-colors"
                      onClick={() => openOpportunityDetail(opp)}
                      data-testid={`pipeline-opp-row-${opp.id}`}
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-10 h-10 rounded-lg flex items-center justify-center"
                            style={{ backgroundColor: selectedStage?.bgColor }}
                          >
                            <Target className="w-5 h-5" style={{ color: selectedStage?.color }} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-[#0F172A]">{opp.account?.university_name}</p>
                              {opp.is_enterprise && (
                                <Badge className="bg-[#7C3AED] text-white text-[10px] px-1.5 py-0 border-0">
                                  ENTERPRISE
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-[#475569]">{opp.sport}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Avatar className="h-7 w-7 border border-[#E2E8F0]">
                            <AvatarImage src={opp.owner?.avatar_url} />
                            <AvatarFallback className="bg-[#F1F5F9] text-[#007AFF] text-xs">
                              {getInitials(opp.owner?.name)}
                            </AvatarFallback>
                          </Avatar>
                          <span className="text-[#475569] text-sm">{opp.owner?.name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {opp.products?.map((p, i) => (
                            <Badge key={i} className="bg-[#F1F5F9] text-[#475569] text-xs border-0">
                              {p.product_type}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <span 
                          className="font-bold"
                          style={{ 
                            fontFamily: "'Barlow Condensed', sans-serif",
                            color: selectedStage?.color 
                          }}
                        >
                          {formatCurrency(opp.total_estimated_premium || 0)}
                        </span>
                      </TableCell>
                      <TableCell className="text-[#475569]">
                        <div className="flex items-center gap-2">
                          {opp.expected_close_date || '-'}
                          <ChevronRight className="w-4 h-4 text-[#94A3B8]" />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </ScrollArea>
          
          {stageOpportunities.length > 0 && (
            <div className="mt-4 pt-4 border-t border-[#E2E8F0] flex justify-between items-center">
              <span className="text-sm text-[#94A3B8]">
                Total: {stageOpportunities.length} opportunities
              </span>
              <span 
                className="text-xl font-bold"
                style={{ 
                  fontFamily: "'Barlow Condensed', sans-serif",
                  color: selectedStage?.color 
                }}
              >
                {formatCurrency(stageOpportunities.reduce((sum, o) => sum + (o.total_estimated_premium || 0), 0))}
              </span>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Opportunity Detail Dialog */}
      <Dialog open={!!selectedOpportunity} onOpenChange={closeOpportunityDetail}>
        <DialogContent className="bg-white border-[#E2E8F0] text-[#0F172A] max-w-2xl max-h-[85vh]" data-testid="opportunity-detail-dialog">
          <DialogHeader>
            <div className="flex items-center gap-2 mb-2">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={closeOpportunityDetail}
                className="text-[#94A3B8] hover:text-[#0F172A] hover:bg-[#F1F5F9] -ml-2"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back to list
              </Button>
            </div>
            <DialogTitle className="text-2xl font-bold" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              OPPORTUNITY DETAILS
            </DialogTitle>
          </DialogHeader>
          
          {selectedOpportunity && (
            <ScrollArea className="max-h-[65vh] pr-4">
              <div className="space-y-6">
                {/* University & Sport Header */}
                <div className="flex items-start gap-4 p-4 bg-[#F8FAFC] rounded-xl border border-[#E2E8F0]">
                  <div 
                    className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: getStageInfo(selectedOpportunity.stage).bgColor }}
                  >
                    <Building2 className="w-7 h-7" style={{ color: getStageInfo(selectedOpportunity.stage).color }} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-[#0F172A]">{selectedOpportunity.account?.university_name}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <Trophy className="w-4 h-4 text-[#F59E0B]" />
                      <span className="text-[#475569]">{selectedOpportunity.sport}</span>
                      {selectedOpportunity.department && (
                        <>
                          <span className="text-[#94A3B8]">•</span>
                          <span className="text-[#475569]">{selectedOpportunity.department}</span>
                        </>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge 
                        className="border-0"
                        style={{ 
                          backgroundColor: getStageInfo(selectedOpportunity.stage).bgColor,
                          color: getStageInfo(selectedOpportunity.stage).color 
                        }}
                      >
                        {selectedOpportunity.stage}
                      </Badge>
                      <Badge 
                        className={`border-0 ${
                          selectedOpportunity.status === 'Closed Won' ? 'bg-[#ECFDF5] text-[#059669]' :
                          selectedOpportunity.status === 'Closed Lost' ? 'bg-[#FEF2F2] text-[#DC2626]' :
                          'bg-[#EFF6FF] text-[#007AFF]'
                        }`}
                      >
                        {selectedOpportunity.status}
                      </Badge>
                      {selectedOpportunity.is_enterprise && (
                        <Badge className="bg-[#7C3AED] text-white border-0">
                          ENTERPRISE
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-[#94A3B8] uppercase tracking-wider">Total Premium</p>
                    <p 
                      className="text-2xl font-bold"
                      style={{ 
                        fontFamily: "'Barlow Condensed', sans-serif",
                        color: getStageInfo(selectedOpportunity.stage).color 
                      }}
                    >
                      {formatCurrency(selectedOpportunity.total_estimated_premium || 0)}
                    </p>
                  </div>
                </div>

                {/* Key Info Grid */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-white rounded-lg border border-[#E2E8F0]">
                    <div className="flex items-center gap-2 mb-2">
                      <User className="w-4 h-4 text-[#94A3B8]" />
                      <span className="text-xs text-[#94A3B8] uppercase tracking-wider font-medium">Owner</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Avatar className="h-8 w-8 border border-[#E2E8F0]">
                        <AvatarImage src={selectedOpportunity.owner?.avatar_url} />
                        <AvatarFallback className="bg-[#F1F5F9] text-[#007AFF] text-xs">
                          {getInitials(selectedOpportunity.owner?.name)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium text-[#0F172A]">{selectedOpportunity.owner?.name}</p>
                        <p className="text-xs text-[#475569]">{selectedOpportunity.owner?.role}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-4 bg-white rounded-lg border border-[#E2E8F0]">
                    <div className="flex items-center gap-2 mb-2">
                      <Calendar className="w-4 h-4 text-[#94A3B8]" />
                      <span className="text-xs text-[#94A3B8] uppercase tracking-wider font-medium">Expected Close</span>
                    </div>
                    <p className="font-medium text-[#0F172A] text-lg">
                      {selectedOpportunity.expected_close_date || 'Not set'}
                    </p>
                  </div>
                </div>

                {/* Account Details */}
                <div className="p-4 bg-white rounded-lg border border-[#E2E8F0]">
                  <div className="flex items-center gap-2 mb-3">
                    <Building2 className="w-4 h-4 text-[#94A3B8]" />
                    <span className="text-xs text-[#94A3B8] uppercase tracking-wider font-medium">Account Information</span>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-[#94A3B8]">Conference</p>
                      <p className="font-medium text-[#0F172A]">{selectedOpportunity.account?.conference || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[#94A3B8]">Division</p>
                      <p className="font-medium text-[#0F172A]">{selectedOpportunity.account?.division || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[#94A3B8]">State</p>
                      <p className="font-medium text-[#0F172A]">{selectedOpportunity.account?.state || '-'}</p>
                    </div>
                  </div>
                  
                  {/* Contacts */}
                  {selectedOpportunity.account?.contacts?.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-[#E2E8F0]">
                      <p className="text-xs text-[#94A3B8] uppercase tracking-wider font-medium mb-3">Contacts</p>
                      <div className="space-y-2">
                        {selectedOpportunity.account.contacts.map((contact, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 bg-[#F8FAFC] rounded-lg">
                            <div>
                              <p className="font-medium text-[#0F172A]">{contact.name || 'Unnamed'}</p>
                              <p className="text-xs text-[#475569]">{contact.role || 'No role'}</p>
                            </div>
                            <div className="flex items-center gap-3 text-sm text-[#475569]">
                              {contact.email && (
                                <a href={`mailto:${contact.email}`} className="flex items-center gap-1 hover:text-[#007AFF]">
                                  <Mail className="w-3 h-3" />
                                  {contact.email}
                                </a>
                              )}
                              {contact.phone && (
                                <a href={`tel:${contact.phone}`} className="flex items-center gap-1 hover:text-[#007AFF]">
                                  <Phone className="w-3 h-3" />
                                  {contact.phone}
                                </a>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Products */}
                <div className="p-4 bg-white rounded-lg border border-[#E2E8F0]">
                  <div className="flex items-center gap-2 mb-3">
                    <Package className="w-4 h-4 text-[#94A3B8]" />
                    <span className="text-xs text-[#94A3B8] uppercase tracking-wider font-medium">Products</span>
                  </div>
                  
                  {selectedOpportunity.products?.length > 0 ? (
                    <div className="space-y-2">
                      {selectedOpportunity.products.map((product, idx) => (
                        <div 
                          key={idx} 
                          className="flex items-center justify-between p-3 bg-[#F8FAFC] rounded-lg border border-[#E2E8F0]"
                        >
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-10 h-10 rounded-lg flex items-center justify-center"
                              style={{ backgroundColor: getStageInfo(selectedOpportunity.stage).bgColor }}
                            >
                              <Package className="w-5 h-5" style={{ color: getStageInfo(selectedOpportunity.stage).color }} />
                            </div>
                            <div>
                              <p className="font-medium text-[#0F172A]">{product.product_type}</p>
                              <Badge 
                                className={`text-xs border-0 ${
                                  product.status === 'Bound' ? 'bg-[#ECFDF5] text-[#059669]' :
                                  product.status === 'Removed' ? 'bg-[#FEF2F2] text-[#DC2626]' :
                                  'bg-[#F1F5F9] text-[#475569]'
                                }`}
                              >
                                {product.status}
                              </Badge>
                            </div>
                          </div>
                          <span 
                            className="text-lg font-bold"
                            style={{ 
                              fontFamily: "'Barlow Condensed', sans-serif",
                              color: getStageInfo(selectedOpportunity.stage).color 
                            }}
                          >
                            {formatCurrency(product.estimated_premium || 0)}
                          </span>
                        </div>
                      ))}
                      
                      <Separator className="my-3" />
                      
                      <div className="flex justify-between items-center px-3">
                        <span className="text-sm font-medium text-[#475569]">Total Estimated Premium</span>
                        <span 
                          className="text-xl font-bold"
                          style={{ 
                            fontFamily: "'Barlow Condensed', sans-serif",
                            color: getStageInfo(selectedOpportunity.stage).color 
                          }}
                        >
                          {formatCurrency(selectedOpportunity.total_estimated_premium || 0)}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <p className="text-[#94A3B8] text-center py-4">No products added yet</p>
                  )}
                </div>

                {/* Notes */}
                {selectedOpportunity.notes && (
                  <div className="p-4 bg-white rounded-lg border border-[#E2E8F0]">
                    <div className="flex items-center gap-2 mb-2">
                      <FileText className="w-4 h-4 text-[#94A3B8]" />
                      <span className="text-xs text-[#94A3B8] uppercase tracking-wider font-medium">Notes</span>
                    </div>
                    <p className="text-[#475569] whitespace-pre-wrap">{selectedOpportunity.notes}</p>
                  </div>
                )}
              </div>
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PipelinePage;
